/**
 * Verbose relative time for list views: "15 Minutes Ago", "Yesterday",
 * "3 Days Ago", then an absolute month/day past ~5 weeks. Compact
 * variant ("5m", "2h") below is for chat-bubble meta lines.
 */
const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;

function pluralize(value: number, singular: string, plural: string): string {
  return `${value} ${value === 1 ? singular : plural} Ago`;
}

export interface FormatVerboseRelativeTimeInput {
  iso?: string | null;
  /** Override for deterministic tests. */
  now?: number;
}

export function formatVerboseRelativeTime({ iso, now }: FormatVerboseRelativeTimeInput): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const reference = now ?? Date.now();
  const diff = Math.max(0, reference - then);

  if (diff < MINUTE_MS) return 'Just Now';
  if (diff < HOUR_MS) {
    const mins = Math.floor(diff / MINUTE_MS);
    return pluralize(mins, 'Minute', 'Minutes');
  }
  if (diff < DAY_MS) {
    const hrs = Math.floor(diff / HOUR_MS);
    return pluralize(hrs, 'Hour', 'Hours');
  }
  if (diff < 2 * DAY_MS) return 'Yesterday';
  if (diff < WEEK_MS) {
    const days = Math.floor(diff / DAY_MS);
    return pluralize(days, 'Day', 'Days');
  }
  const weeks = Math.floor(diff / WEEK_MS);
  if (weeks < 5) {
    return pluralize(weeks, 'Week', 'Weeks');
  }
  return new Date(then).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function formatCompactRelativeTime({ iso, now }: FormatVerboseRelativeTimeInput): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const reference = now ?? Date.now();
  const diff = Math.max(0, reference - then);

  if (diff < MINUTE_MS) return 'now';
  if (diff < HOUR_MS) return `${Math.floor(diff / MINUTE_MS)}m`;
  if (diff < DAY_MS) return `${Math.floor(diff / HOUR_MS)}h`;
  if (diff < WEEK_MS) return `${Math.floor(diff / DAY_MS)}d`;
  return new Date(then).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
